import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";


/* ─── Data ──────────────────────────────────────────────────────────── */

const conditionCards = [
  {
    title: 'High Blood Pressure',
    desc: 'Blood pressure of 130/80 mm Hg or above. Often symptom-free until it has already raised the risk of stroke, heart disease, or kidney damage.',
    iconBg: '#FADBD8',
    iconColor: '#C0392B',
    iconPath: require('../assets/symptoms/highBPIcon.png'),
  },
  {
    title: 'High Cholesterol',
    desc: 'Excess LDL cholesterol causes plaque to build inside arteries, increasing the probability of heart attack and stroke.',
    iconBg: '#FAEEDA',
    iconColor: '#854F0B',
    iconPath: require('../assets/symptoms/highCholestrol.png'),
  },
  {
    title: 'Dyslipidemia',
    desc: 'Unhealthy blood fat — high LDL, low HDL, high triglycerides, or a combination. Usually discovered during a routine blood test.',
    iconBg: '#EAF3DE',
    iconColor: '#3B6D11',
    iconPath: require('../assets/symptoms/dyslip.png'),
  },
  {
    title: 'Cardiovascular Risk',
    desc: 'When both conditions are elevated together, their combined effect on blood vessels is far more damaging than either alone.',
    iconBg: '#E6F1FB',
    iconColor: '#185FA5',
    iconPath: require('../assets/symptoms/cardiovascular.png'),
  },
];

const riskPlanItems = [
  'Home blood pressure measurements and tracking over time',
  'Lipid blood tests to monitor cholesterol and triglyceride levels',
  'Eating and exercise counseling tailored to your risk profile',
  'Weight management support for sustainable progress',
  'Smoking cessation guidance and accountability',
  'Regular review and adjustment of medications',
  'Follow-up monitoring to minimize long-term cardiovascular risk',
];

const bpTreatmentItems = [
  'Reducing salt intake and following a heart-healthy diet',
  'Increasing physical activity and daily movement',
  'Achieving or maintaining a healthy body weight',
  'Reducing alcohol consumption and quitting smoking',
  'Improving sleep quality and managing stress',
  'ACE inhibitors, calcium channel blockers, diuretics, or beta blockers as needed',
];

const cholesterolTreatmentItems = [
  'Heart-healthy eating rich in fibre, healthy fats, and whole foods',
  'Increased physical activity to raise HDL and lower triglycerides',
  'Weight loss where necessary to improve lipid levels',
  'Better diabetes control to reduce secondary dyslipidemia',
  'Statin therapy or alternatives such as ezetimibe or bempedoic acid',
  'Re-testing at appropriate intervals to monitor progress',
];

const qaItems = [
  {
    q: 'Why treat blood pressure and cholesterol together?',
    a: 'Both are major risk factors for heart disease. Controlling one while leaving the other elevated is insufficient — comprehensive cardiovascular risk management considers the full picture.',
  },
  {
    q: 'Can virtual visits really help manage these conditions?',
    a: 'Yes. Medication response is frequently assessed through home readings and follow-up rather than emergency symptoms, making virtual care highly effective for gradual adjustments.',
  },
  {
    q: 'When should I see a cardiovascular specialist?',
    a: 'If blood pressure or cholesterol does not respond to treatment, if you have diabetes, kidney disease, existing heart disease, or high family risk — specialist review is recommended.',
  },
];

/* ─── Sub-components ────────────────────────────────────────────────── */

function HeroBanner() {
  return (
    <div className="ihb-hero">
      <img
        src={require('../assets/symptoms/highBP.png')}
        alt="Chest pain and cardiovascular care"
        onError={(e) => {
          e.target.style.display = 'none';
        }}
      />
      <div className="ihb-hero-overlay" />
      <div className="ihb-hero-content">
        <span className="ihb-hero-tag">Cardiovascular Health</span>
        <h1>High Blood Pressure &amp; Cholesterol</h1>
        <p>
          Two of the most prevalent cardiovascular risk factors — silently damaging
          blood vessels over time, yet highly manageable with the right plan.
        </p>
      </div>
    </div>
  );
}

function Byline() {
  return (
    <div className="ihb-byline">
      <div className="ihb-byline-dot">CV</div>
      <div className="ihb-byline-text">
        <strong>Cardiovascular Physician — Dr. Siam Al Mobarak</strong>
        <span>
          Expert guidance on hypertension, cholesterol management &amp;
          cardiovascular risk
        </span>
      </div>
    </div>
  );
}

function ConditionCard({ title, desc, iconBg, iconColor, iconPath }) {
  return (
    <div className="ihb-rhythm-card">
      <div className="ihb-icon" >
        <img src={iconPath} alt="" />
      </div>
      <h3>{title}</h3>
      <p>{desc}</p>
    </div>
  );
}

function QAItem({ q, a }) {
  return (
    <div className="ihb-qa-item">
      <div className="ihb-qa-q">{q}</div>
      <div className="ihb-qa-a">{a}</div>
    </div>
  );
}

function BulletList({ items }) {
  return (
    <ul className="ihb-treatment-list">
      {items.map((item) => (
        <li key={item}>
          <span className="ihb-bullet" aria-hidden="true" />
          {item}
        </li>
      ))}
    </ul>
  );
}

/* ─── Main Page Component ───────────────────────────────────────────── */

export default function BloodPressureCholesterolPage() {
  const navigate = useNavigate();

  useEffect(() => {
    const description = "Expert cardiovascular disease management and cardiac patient care. Personalized strategies to reduce risks and improve heart health for lasting wellness.";

    let meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute('content', description);
    } else {
      meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = description;
      document.head.appendChild(meta);
    }

    document.title = 'Cardiac Patient & Cardiovascular Disease Management';
  }, []);

  return (
    <>
      <Helmet>
        <title>Cardiac Patient &amp; Cardiovascular Disease Management</title>
        <meta
          name="description"
          content="Expert cardiovascular disease management and cardiac patient care. Personalized strategies to reduce risks and improve heart health for lasting wellness."
        />
      </Helmet>

      <div className="ihb-body">

        {/* ── Hero ── */}
        <HeroBanner />

        <div className="ihb-page">

        {/* ── Byline ── */}
        <Byline />

        {/* ── Lead ── */}
        <p className="ihb-lead">
          High blood pressure and high cholesterol may not produce symptoms
          initially, but they silently destroy blood vessels over time. Early
          diagnosis, regular check-ups, and a clear treatment plan are essential —
          and virtual follow-up care makes long-term management more accessible than
          ever.
        </p>

        {/* ── Overview ── */}
        <section className="ihb-section">
          <h2>Two Conditions, One Clear Goal</h2>
          <p>
            Most patients feel perfectly well despite having elevated blood pressure
            or unhealthy cholesterol levels. That absence of symptoms is precisely
            what makes these conditions so dangerous. Virtual follow-up care is well
            suited to both, since they require constant review, medication monitoring,
            and long-term lifestyle support.
          </p>
          <p>
            Treating both conditions together is one of the most effective methods
            for reducing the risk of heart attack, stroke, kidney disease, and other
            serious cardiovascular events. Good cardiovascular care considers the
            whole picture — blood pressure, cholesterol, diabetes, weight, smoking,
            family history, kidney function, and any existing heart disease.
          </p>
        </section>

        {/* ── Condition Cards ── */}
        <section className="ihb-section">
          <h2>Conditions We Help Manage</h2>
          <div className="ihb-card-grid">
            {conditionCards.map((card) => (
              <ConditionCard key={card.title} {...card} />
            ))}
          </div>
        </section>

        {/* ── Understanding BP ── */}
        <section className="ihb-section">
          <h2>Understanding High Blood Pressure &amp; Hypertension</h2>
          <p>
            Blood pressure is the force of blood pressing against artery walls. A
            reading of 130/80 mm Hg or above is considered high. High blood pressure
            and hypertension refer to the same chronic condition. The threat is that
            it frequently brings about no apparent warning signs until it has already
            increased the risk of stroke, heart disease, kidney damage, or vision
            problems — which is why regular check-ups are necessary even when you
            feel fine.
          </p>
          <p>
            The most typical risk factors include age, family history, excess salt
            consumption, excess weight, physical inactivity, alcohol, smoking, sleep
            apnea, and stress. Home blood pressure monitoring is valuable because it
            allows tracing patterns over time and often provides a more accurate
            picture than a single reading in a clinical setting. Virtual consultation
            is particularly effective here, since many treatment decisions are based
            on multiple readings and medication tolerance rather than a single visit.
          </p>
        </section>

        {/* ── Understanding Cholesterol ── */}
        <section className="ihb-section">
          <h2>Understanding High Cholesterol &amp; Dyslipidemia</h2>
          <p>
            Cholesterol is a waxy substance in the blood. The body requires some
            cholesterol, but excessive LDL — commonly referred to as the bad
            cholesterol — causes plaque to build inside the arteries. High LDL
            increases the probability of heart attack and stroke. The medical term
            for unhealthy blood fat levels is dyslipidemia, which can include high
            LDL, low HDL, high triglycerides, or a combination of these.
          </p>
          <p>
            Like high blood pressure, high cholesterol is normally asymptomatic and
            most often discovered during a routine blood test. It may stem from
            genetics, diet, inactivity, diabetes, thyroid disease, kidney disease,
            menopause, or certain medications. Since there are no symptoms, monitoring
            is as important as treatment — making virtual review particularly useful
            for discussing lab results, adjusting medications, and encouraging
            gradual lifestyle change.
          </p>
        </section>

        {/* ── Callout ── */}
        <div className="ihb-callout" role="note">
          <p>
            <strong>Important:</strong> When both blood pressure and cholesterol are
            elevated simultaneously, their combined effect on blood vessels is
            significantly more damaging than either condition alone. Both are
            recognized as major independent risk factors for heart disease — treating
            only one is not enough.
          </p>
        </div>

        {/* ── Comprehensive Risk Plan ── */}
        <section className="ihb-section">
          <h2>A Comprehensive Cardiovascular Risk Plan</h2>
          <p>
            This comprehensive approach is referred to as cardiovascular risk
            management. Rather than addressing blood pressure and cholesterol as
            separate, unrelated problems, it considers the full clinical picture —
            and it is far more effective as a result. A risk plan may include:
          </p>
          <BulletList items={riskPlanItems} />
          <p style={{ marginTop: '1rem' }}>
            A cardiovascular clinic that monitors both conditions simultaneously —
            rather than treating them as independent issues — provides the most
            meaningful benefit for patients living with multiple risk factors.
          </p>
        </section>

        {/* ── BP Treatment ── */}
        <section className="ihb-section">
          <h2>High Blood Pressure Treatment</h2>
          <p>
            Hypertension treatment typically starts with lifestyle modifications, and
            a good number of patients also require medication. Healthy living and,
            where appropriate, blood pressure medicine can help improve control over
            time. Common lifestyle measures include:
          </p>
          <BulletList items={bpTreatmentItems} />
          <p style={{ marginTop: '1rem' }}>
            Virtual care is effective for many individuals because the response to
            medication is frequently assessed through home measurements and follow-up
            rather than emergency symptoms — enabling gradual adjustment with minimal
            disruption to daily life.
          </p>
        </section>

        {/* ── Cholesterol Management ── */}
        <section className="ihb-section">
          <h2>Cholesterol Management</h2>
          <p>
            Cholesterol management is grounded in lifestyle change, yet many patients
            require medication to achieve safer levels. Statins are the most widely
            used medication for high cholesterol, with alternatives such as
            ezetimibe, bempedoic acid, or injectable therapy available for those who
            need them. Current guidelines emphasize holistic, risk-based management
            of abnormal cholesterol. Key parts of treatment include:
          </p>
          <BulletList items={cholesterolTreatmentItems} />
        </section>

        {/* ── Q&A ── */}
        <section className="ihb-section">
          <h2>Common Questions</h2>
          <div className="ihb-qa-section">
            {qaItems.map((item) => (
              <QAItem key={item.q} {...item} />
            ))}
          </div>
        </section>

        {/* ── Urgent Box ── */}
        <div className="ihb-urgent-box" role="note">
          <h2>When to See a Cardiovascular Specialist</h2>
          <p>
            Consider specialist review when your blood pressure does not respond to
            treatment, when cholesterol does not improve despite medication, when you
            have diabetes or kidney disease, or when you already have heart disease,
            a history of stroke, chest pain, shortness of breath, or significant
            family risk. A cardiovascular specialist can develop a more personalized
            prevention strategy and determine when testing, medication changes, or
            closer follow-up is needed.
          </p>
        </div>

        {/* ── Long-term Monitoring ── */}
        <section className="ihb-section">
          <h2>Continuous Monitoring &amp; Prevention</h2>
          <p>
            Long-term prevention is best achieved steadily, not in a hurry. Virtual
            visits support this by enabling regular medication reviews, home reading
            checks, lab result discussions, and habit reinforcement — without
            requiring frequent in-person appointments. Through the right strategy,
            many patients can meaningfully improve control and reduce future risk.
          </p>
          <p>
            Working with a skilled cardiovascular team focused on prevention can
            transform numbers into action — whether that means blood pressure
            support, lipid checks, or a full cardiovascular risk plan. Prevention
            becomes more straightforward, personal, and effective when managed
            consistently over time.
          </p>
        </section>

        {/* ── Footer CTA ── */}
        <div className="ihb-footer-cta">
          <p>
            Managing high blood pressure or cholesterol — or both? Start a virtual
            review today and take a confident step toward better cardiovascular
            health, lower risk, and a clearer plan.
          </p>
          <button className="ihb-cta-btn" onClick={() => navigate('/consultations')}>Start Your Virtual Review</button>
        </div>

        </div>
      </div>
    </>
  );
}
